import { persistStore, persistReducer, createTransform } from 'redux-persist';
import { createStore, applyMiddleware } from 'redux';
import { createLogger } from 'redux-logger';
import thunk from 'redux-thunk';
import { composeWithDevTools } from 'redux-devtools-extension';
import storage from 'redux-persist/lib/storage';
import Bugsnag from '@bugsnag/js';

import rootReducer from './reducers';

// The whole store is serialized into ONE localStorage key, so a single oversized
// slice fails the write for everything else. A user profile can carry a base64
// `logo` (megabytes) and a legacy self-nested `data` duplicate — one account hit
// 7MB, blowing the ~5MB quota, which silently froze the persisted state (and with
// it the order list the Chrome extension reads). Neither field is read anywhere,
// so they are dropped on the way to storage only; the in-memory user object and
// the profile save round-trip are untouched.
// Arrays are walked too: a logo nested inside one would otherwise survive by
// reference and put the payload straight back over quota. The depth cap is a
// cycle guard, not a policy.
const stripLogos = (val, depth = 0) => {
  if (!val || typeof val !== 'object' || depth > 8) { return val; }
  if (Array.isArray(val)) { return val.map(v => stripLogos(v, depth + 1)); }
  const out = {};
  Object.keys(val).forEach(k => {
    if (k === 'logo') { return; }
    out[k] = stripLogos(val[k], depth + 1);
  });
  return out;
};

const slimUserForStorage = (user) => {
  const slim = stripLogos(user);
  // `data` holds the flattened profile and IS read (data.company.* — card #11
  // round 16), but a `data.data` beneath it is only a self-nested duplicate of
  // the level above. Target that one key rather than every nested `data`, which
  // would also take unrelated ones such as user.invites[].data.
  if (slim && slim.data) { delete slim.data.data; }
  return slim;
};

const slimUser = createTransform(
  (inbound, key) => (key === 'user' ? slimUserForStorage(inbound) : inbound),
  outbound => outbound,
  { whitelist: ['user'] },
);

// redux-persist 5.x hardcodes its write-failure handler to log only outside
// production, so a full quota went completely unreported in deployed builds.
// Wrap storage to always record it. Logged for diagnostics only — never surfaced
// to the user, who can keep working from in-memory state.
const loggingStorage = {
  ...storage,
  setItem: (key, value) => storage.setItem(key, value).catch(err => {
    try {
      console.error(`[persist] write failed for "${key}" (${(value || '').length} chars) — stored state is now STALE`, err);
      Bugsnag.notify(err, event => {
        event.addMetadata('persist', { key, chars: (value || '').length });
      });
    } catch (e) { /* diagnostics must never break the app */ }
  }),
};

const persistConfig = {
  key: 'GPS_root',
  storage: loggingStorage,
  blacklist: ['view'],
  transforms: [slimUser],
};

const persistedReducer = persistReducer(persistConfig, rootReducer);

const middleware = [thunk, createLogger()];

const enhancer = composeWithDevTools(applyMiddleware(...middleware));

export const configureStore = () => {
  const store = createStore(persistedReducer, enhancer);
  const persistor = persistStore(store);
  window.STORE = store;
  return { store, persistor };
}